// Administração > Conta & Integrações > Webhook & API (pedido 2026-09-23).
// Uma configuração por empresa, criada no backend na primeira abertura
// desta tela. Ver gpsdrive-company-webhook.service.ts.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Check,
  ChevronDown,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  Send,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { ALERT_TYPE_OPTIONS, type AlertType } from "@/services/alert-rules.service";
import {
  gpsDriveCompanyWebhookService,
  type CompanyWebhook,
  type UpdateCompanyWebhookInput,
} from "@/services/gpsdrive-company-webhook.service";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/webhooks")({
  component: () => (
    <AdminAccessGate action="conta_integracoes">
      <WebhooksPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Webhook & API — Administração — Wp Rastreamento" }] }),
});

const KEY = ["company-webhook"] as const;
const LOGS_KEY = ["company-webhook", "logs"] as const;

function errorText(err: unknown): string {
  if (err instanceof GpsDriveApiError || err instanceof Error) return err.message;
  return "Não foi possível salvar. Tente novamente.";
}

function WebhooksPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: KEY,
    queryFn: () => gpsDriveCompanyWebhookService.get(),
    retry: false,
  });
  const setData = (w: CompanyWebhook) => qc.setQueryData(KEY, w);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Webhook &amp; API</h1>
        <p className="text-sm text-muted-foreground">
          Integre a plataforma com outros sistemas: receba posições de dispositivos (por exemplo, tags de outra
          plataforma) e envie os alertas da sua frota para o sistema da empresa em tempo real.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
        </div>
      ) : error || !data ? (
        <p className="text-sm text-destructive">{errorText(error)}</p>
      ) : (
        <>
          <InboundCard hook={data} onChange={setData} />
          <OutboundCard hook={data} onChange={setData} />
          <LogsCard />
        </>
      )}
    </div>
  );
}

// --- Entrada ------------------------------------------------------------------

function InboundCard({ hook, onChange }: { hook: CompanyWebhook; onChange: (w: CompanyWebhook) => void }) {
  const [err, setErr] = useState<string | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const toggle = useMutation({
    mutationFn: (inboundEnabled: boolean) => gpsDriveCompanyWebhookService.update({ inboundEnabled }),
    onSuccess: (w) => {
      setErr(null);
      onChange(w);
    },
    onError: (e) => setErr(errorText(e)),
  });
  const rotate = useMutation({
    mutationFn: () => gpsDriveCompanyWebhookService.rotateInboundToken(),
    onSuccess: (w) => {
      setErr(null);
      onChange(w);
    },
    onError: (e) => setErr(errorText(e)),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-emerald-500/10 text-emerald-600">
              <ArrowDownToLine className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Receber posições (entrada)</CardTitle>
              <CardDescription>
                Outra plataforma envia a posição dos dispositivos para cá — ideal para tags e rastreadores
                integrados por API. Só são aceitos dispositivos já cadastrados na empresa, com o mesmo
                IMEI/identificador.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="wh-in" className="text-sm">
              {hook.inboundEnabled ? "Ativo" : "Desativado"}
            </Label>
            <Switch
              id="wh-in"
              checked={hook.inboundEnabled}
              disabled={toggle.isPending}
              onCheckedChange={(v) => toggle.mutate(v)}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label>Endereço de entrada (POST)</Label>
          <div className="flex gap-2">
            <Input readOnly value={hook.inboundUrl} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
            <CopyButton text={hook.inboundUrl} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              A chave de acesso já faz parte do endereço — trate como senha.{" "}
              {hook.lastInboundAt
                ? `Última chamada ${formatDistanceToNow(new Date(hook.lastInboundAt), { addSuffix: true, locale: ptBR })}.`
                : "Nenhuma chamada recebida ainda."}
            </span>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setConfirmRotate(true)}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Gerar novo endereço
            </Button>
          </div>
        </div>

        {err && <p className="text-sm text-destructive">{err}</p>}

        <DocSection title="Como integrar (documentação para o programador)" copyText={inboundDoc(hook.inboundUrl)}>
          <InboundDocBody url={hook.inboundUrl} />
        </DocSection>
      </CardContent>

      <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerar novo endereço de entrada?</AlertDialogTitle>
            <AlertDialogDescription>
              O endereço atual para de funcionar na hora. Qualquer sistema que esteja enviando posições precisará
              ser atualizado com o novo endereço.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => rotate.mutate()}>Gerar novo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function inboundExample(): string {
  return JSON.stringify(
    {
      positions: [
        {
          uniqueId: "000003092500001",
          latitude: -23.550520,
          longitude: -46.633308,
          timestamp: "2026-09-23T14:05:00Z",
          speed: 0,
          course: 0,
          battery: 87,
        },
      ],
    },
    null,
    2,
  );
}

function inboundDoc(url: string): string {
  return `# GPS Drive — Envio de posições (webhook de entrada)

Endpoint (POST, JSON):
${url}

A chave de acesso está no próprio endereço. Não precisa de outro cabeçalho de autenticação.

## Corpo
Aceita uma posição, uma lista, ou { "positions": [ ... ] } (até 500 por requisição).

${inboundExample()}

## Campos
- uniqueId (obrigatório): identificador do dispositivo exatamente como cadastrado na GPS Drive (IMEI / ID da tag). Também aceita "imei" ou "id".
- latitude / longitude (obrigatórios): graus decimais. Também aceita "lat" e "lng"/"lon".
- timestamp: data/hora da posição em ISO 8601 (UTC) ou Unix (segundos ou milissegundos). Sem timestamp = horário do recebimento.
- speed: km/h (opcional)
- course: direção em graus, 0-360 (opcional)
- altitude: metros (opcional)
- accuracy: precisão em metros (opcional)
- battery: nível de bateria em % (opcional)
- ignition: true/false (opcional)
- attributes: objeto com campos extras (opcional)

## Resposta (HTTP 200)
{ "received": 1, "accepted": 1, "rejected": 0, "results": [ { "index": 0, "id": "000003092500001", "ok": true } ] }
Posições recusadas vêm com "ok": false e o motivo em "error" (ex.: dispositivo não cadastrado).

## Erros
- 404: endereço/chave inválido
- 403: recebimento desativado pela empresa
- 400: corpo vazio ou inválido
- 413: mais de 500 posições numa requisição
- 429: mais de 600 requisições por minuto

## Exemplo (curl)
curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -d '{"uniqueId":"000003092500001","latitude":-23.55052,"longitude":-46.633308,"timestamp":"2026-09-23T14:05:00Z","battery":87}'
`;
}

function InboundDocBody({ url }: { url: string }) {
  return (
    <div className="space-y-3 text-sm">
      <p>
        Faça <b>POST</b> com <code>Content-Type: application/json</code> no endereço acima. Pode enviar uma posição,
        uma lista ou <code>{"{ positions: [...] }"}</code> — até 500 por requisição.
      </p>
      <Code>{inboundExample()}</Code>
      <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
        <li>
          <b className="text-foreground">uniqueId</b> (obrigatório): o mesmo IMEI/ID cadastrado no veículo/tag aqui na
          plataforma. Também aceita <code>imei</code> ou <code>id</code>.
        </li>
        <li>
          <b className="text-foreground">latitude / longitude</b> (obrigatórios). Também aceita <code>lat</code> e{" "}
          <code>lng</code>/<code>lon</code>.
        </li>
        <li>
          <b className="text-foreground">timestamp</b>: ISO 8601 (UTC) ou Unix. Sem ele, usa a hora do recebimento.
        </li>
        <li>
          Opcionais: <code>speed</code> (km/h), <code>course</code> (graus), <code>altitude</code>, <code>accuracy</code>,{" "}
          <code>battery</code> (%), <code>ignition</code>, <code>attributes</code>.
        </li>
      </ul>
      <p className="text-muted-foreground">
        Resposta: <code>{'{ "received", "accepted", "rejected", "results": [...] }'}</code> — cada posição recusada vem
        com o motivo. Limite: 600 requisições por minuto.
      </p>
      <Code>{`curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -d '{"uniqueId":"000003092500001","latitude":-23.55052,"longitude":-46.633308,"battery":87}'`}</Code>
    </div>
  );
}

// --- Saída --------------------------------------------------------------------

function OutboundCard({ hook, onChange }: { hook: CompanyWebhook; onChange: (w: CompanyWebhook) => void }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(hook.outboundUrl ?? "");
  const [events, setEvents] = useState<AlertType[]>(hook.outboundEvents);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);

  useEffect(() => {
    setUrl(hook.outboundUrl ?? "");
    setEvents(hook.outboundEvents);
  }, [hook.outboundUrl, hook.outboundEvents]);

  const dirty = (url.trim() || null) !== (hook.outboundUrl ?? null) || !sameSet(events, hook.outboundEvents);

  const save = useMutation({
    mutationFn: (input: UpdateCompanyWebhookInput) => gpsDriveCompanyWebhookService.update(input),
    onSuccess: (w) => {
      setErr(null);
      onChange(w);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setErr(errorText(e)),
  });
  const rotate = useMutation({
    mutationFn: () => gpsDriveCompanyWebhookService.rotateSecret(),
    onSuccess: (w) => onChange(w),
    onError: (e) => setErr(errorText(e)),
  });
  const test = useMutation({
    mutationFn: () => gpsDriveCompanyWebhookService.sendTest(),
    onSettled: () => void qc.invalidateQueries({ queryKey: LOGS_KEY }),
  });

  const allSelected = events.length === 0;
  const toggleEvent = (key: AlertType, on: boolean) => {
    const base = allSelected ? ALERT_TYPE_OPTIONS.map((o) => o.key) : events;
    const next = on ? [...new Set([...base, key])] : base.filter((k) => k !== key);
    // Todos marcados = lista vazia (inclui tipos futuros automaticamente).
    setEvents(next.length === ALERT_TYPE_OPTIONS.length ? [] : next);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-sky-500/10 text-sky-600">
              <ArrowUpFromLine className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Enviar eventos (saída)</CardTitle>
              <CardDescription>
                Quando um alerta dispara (ignição, cerca, excesso de velocidade, bloqueio…), a plataforma envia um POST
                com os dados do veículo e da posição para o endereço do seu sistema.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="wh-out" className="text-sm">
              {hook.outboundEnabled ? "Ativo" : "Desativado"}
            </Label>
            <Switch
              id="wh-out"
              checked={hook.outboundEnabled}
              disabled={save.isPending || (!hook.outboundUrl && !hook.outboundEnabled)}
              onCheckedChange={(v) => save.mutate({ outboundEnabled: v })}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="wh-url">URL de destino</Label>
          <Input
            id="wh-url"
            placeholder="https://seu-sistema.com.br/webhooks/gpsdrive"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          {!hook.outboundUrl && (
            <p className="text-xs text-muted-foreground">Salve uma URL para poder ativar o envio.</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Eventos enviados</Label>
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              onClick={() => setEvents([])}
            >
              Marcar todos
            </button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {ALERT_TYPE_OPTIONS.map((o) => {
              const checked = allSelected || events.includes(o.key);
              return (
                <label
                  key={o.key}
                  className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted/50"
                >
                  <Checkbox checked={checked} onCheckedChange={(v) => toggleEvent(o.key, v === true)} />
                  <span>
                    {o.emoji} {o.label}
                  </span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            Só é enviado alerta de regra configurada no veículo (Alertas do veículo / Notificações do cliente).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate({ outboundUrl: url.trim() || null, outboundEvents: events })}
          >
            {save.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
          <Button
            variant="outline"
            disabled={!hook.outboundUrl || dirty || test.isPending}
            onClick={() => test.mutate()}
            title={dirty ? "Salve antes de testar" : undefined}
          >
            {test.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
            Enviar teste
          </Button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-emerald-600">
              <Check className="h-4 w-4" /> Salvo
            </span>
          )}
          {test.data && (
            <span className={cn("text-sm", test.data.ok ? "text-emerald-600" : "text-destructive")}>
              {test.data.ok
                ? `Teste entregue (HTTP ${test.data.httpStatus})`
                : `Falhou: ${test.data.httpStatus ? `HTTP ${test.data.httpStatus}` : test.data.error}`}
            </span>
          )}
          {test.error && <span className="text-sm text-destructive">{errorText(test.error)}</span>}
        </div>
        {err && <p className="text-sm text-destructive">{err}</p>}
        {hook.lastOutboundAt && (
          <p className="text-xs text-muted-foreground">
            Último envio {formatDistanceToNow(new Date(hook.lastOutboundAt), { addSuffix: true, locale: ptBR })} —{" "}
            {hook.lastOutboundStatus}
          </p>
        )}

        <div className="space-y-1.5">
          <Label>Segredo de assinatura</Label>
          <div className="flex gap-2">
            <Input
              readOnly
              type={showSecret ? "text" : "password"}
              value={hook.outboundSecret}
              className="font-mono text-xs"
            />
            <Button variant="outline" size="icon" onClick={() => setShowSecret((s) => !s)} aria-label="Mostrar segredo">
              {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <CopyButton text={hook.outboundSecret} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>Use para confirmar que o envio veio mesmo da GPS Drive (cabeçalho X-GPSDrive-Signature).</span>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setConfirmRotate(true)}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Gerar novo segredo
            </Button>
          </div>
        </div>

        <DocSection title="Formato do envio (documentação para o programador)" copyText={outboundDoc()}>
          <OutboundDocBody />
        </DocSection>
      </CardContent>

      <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerar novo segredo?</AlertDialogTitle>
            <AlertDialogDescription>
              Os próximos envios serão assinados com o novo segredo. O sistema que recebe precisa ser atualizado, senão
              vai recusar as assinaturas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => rotate.mutate()}>Gerar novo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function outboundExample(): string {
  return JSON.stringify(
    {
      id: "8f0c2a1e-5b7d-4c55-9a1e-2f3d4c5b6a7e",
      event: "alert.ignition_on",
      type: "ignition_on",
      message: "Ignição ligada — HB20 ABC1D23",
      occurredAt: "2026-09-23T14:05:12.000Z",
      vehicle: { id: 5022, name: "HB20 ABC1D23", uniqueId: "863238076509266", plate: "ABC1D23" },
      position: {
        latitude: -23.55052,
        longitude: -46.633308,
        speedKmh: 0,
        course: 90,
        fixTime: "2026-09-23T14:05:10.000Z",
        ignition: true,
        mapUrl: "https://www.google.com/maps?q=-23.55052,-46.633308",
      },
    },
    null,
    2,
  );
}

const TYPES_DOC = ALERT_TYPE_OPTIONS.map((o) => `- ${o.key}: ${o.label}`).join("\n");

const VERIFY_SNIPPET = `// Node.js — verificar a assinatura
const crypto = require("crypto");
function assinaturaValida(req, rawBody, segredo) {
  const ts = req.headers["x-gpsdrive-timestamp"];
  const esperado = "sha256=" + crypto.createHmac("sha256", segredo).update(ts + "." + rawBody).digest("hex");
  return crypto.timingSafeEqual(Buffer.from(esperado), Buffer.from(req.headers["x-gpsdrive-signature"] || ""));
}`;

function outboundDoc(): string {
  return `# GPS Drive — Webhook de saída (eventos)

A cada alerta disparado, a GPS Drive faz POST (JSON) na URL configurada.
Responda com HTTP 2xx em até 8 segundos. Em falha de rede ou HTTP 5xx há mais 2 tentativas (após 2s e 10s). HTTP 4xx não é repetido.

## Cabeçalhos
- X-GPSDrive-Event: tipo do evento (ex.: alert.ignition_on, ou "test" no botão Enviar teste)
- X-GPSDrive-Timestamp: Unix em segundos
- X-GPSDrive-Signature: sha256=HMAC_SHA256(segredo, timestamp + "." + corpo_bruto) em hexadecimal

## Corpo
${outboundExample()}

"position" pode ser null se o veículo ainda não tiver posição. Use "id" para ignorar entregas repetidas.

## Tipos (campo "type")
${TYPES_DOC}

## Verificação da assinatura
${VERIFY_SNIPPET}
`;
}

function OutboundDocBody() {
  return (
    <div className="space-y-3 text-sm">
      <p className="text-muted-foreground">
        POST com JSON, esperando resposta HTTP 2xx em até 8s. Falha de rede ou 5xx: mais 2 tentativas (2s e 10s). Use o
        campo <code>id</code> para ignorar entregas repetidas.
      </p>
      <Code>{outboundExample()}</Code>
      <p className="text-muted-foreground">
        Cabeçalhos: <code>X-GPSDrive-Event</code>, <code>X-GPSDrive-Timestamp</code> e <code>X-GPSDrive-Signature</code>{" "}
        = <code>sha256=</code>HMAC-SHA256 do segredo sobre <code>timestamp + "." + corpo</code>.
      </p>
      <Code>{VERIFY_SNIPPET}</Code>
    </div>
  );
}

// --- Histórico ---------------------------------------------------------------

function LogsCard() {
  const { data, isFetching, refetch } = useQuery({
    queryKey: LOGS_KEY,
    queryFn: () => gpsDriveCompanyWebhookService.logs(),
    refetchInterval: 30_000,
    retry: false,
  });
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-lg">Histórico</CardTitle>
          <CardDescription>Últimas chamadas recebidas e enviadas (7 dias).</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={cn("mr-1 h-4 w-4", isFetching && "animate-spin")} /> Atualizar
        </Button>
      </CardHeader>
      <CardContent>
        {!data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma chamada ainda.</p>
        ) : (
          <div className="divide-y rounded-md border">
            {data.map((l) => (
              <div key={l.id} className="flex items-start gap-3 px-3 py-2 text-sm">
                <Badge
                  variant="outline"
                  className={cn(
                    "mt-0.5 shrink-0",
                    l.direction === "in" ? "border-emerald-500/40 text-emerald-700" : "border-sky-500/40 text-sky-700",
                  )}
                >
                  {l.direction === "in" ? "Entrada" : "Saída"}
                </Badge>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn("h-2 w-2 shrink-0 rounded-full", l.ok ? "bg-emerald-500" : "bg-red-500")} />
                    <span className="break-all">{l.summary}</span>
                  </div>
                  {l.detail && (
                    <pre className="mt-1 whitespace-pre-wrap break-all text-xs text-muted-foreground">{l.detail}</pre>
                  )}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {new Date(l.createdAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" })}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- UI helpers ---------------------------------------------------------------

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size={label ? "sm" : "icon"}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard bloqueado — o texto continua selecionável na tela */
        }
      }}
      aria-label="Copiar"
    >
      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      {label && <span className="ml-1">{copied ? "Copiado" : label}</span>}
    </Button>
  );
}

function DocSection({ title, copyText, children }: { title: string; copyText: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <CollapsibleTrigger className="flex flex-1 items-center gap-2 text-left text-sm font-medium">
          <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
          {title}
        </CollapsibleTrigger>
        <CopyButton text={copyText} label="Copiar documentação" />
      </div>
      <CollapsibleContent className="border-t px-3 py-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-md bg-slate-950 p-3 font-mono text-xs leading-relaxed text-slate-100">
      {children}
    </pre>
  );
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}
